import { useCallback, useEffect, useContext } from 'react';
import { CloudContext } from '../context/CloudContext';
import { AuthContext } from '../context/AuthContext';
import config from '../config.js';
import { createShortUrl, deleteShortUrl } from './urlShortener.js';
import { encodePair } from '../viewer/utils/transformationCodec.js';

// Cesium Ion REST API base URL
const CESIUM_API_BASE = 'https://api.cesium.com';

/**
 * Create a new Cesium Ion access token
 * @param {Object} tokenData - Token configuration
 * @param {string} tokenData.name - Token name
 * @param {number} tokenData.expirationDays - Days until expiration
 * @param {string} tokenData.assetId - Asset ID to grant access to
 * @param {Array<string>} tokenData.scopes - Token scopes (e.g., ['assets:read'])
 * @param {string} cesiumToken - Master Cesium token for API calls
 * @returns {Promise<Object>} Created token data
 */
export const createCesiumToken = async (tokenData, cesiumToken) => {
  const { name, assetId, gaussianSplattingId, resultsId, scopes = ['assets:read'] } = tokenData;
  
  if (!cesiumToken) {
    throw new Error('No Cesium master token available');
  }
  
  if (!assetId) {
    throw new Error('Asset ID is required');
  }
  
  // Build assetIds array - include gaussian splatting asset if provided
  const assetIds = [parseInt(assetId), ...config.cesiumBasemapIDs];
  if (gaussianSplattingId) {
    assetIds.push(parseInt(gaussianSplattingId));
  }

  if (resultsId && Array.isArray(resultsId) && resultsId.length > 0) {
    // Add each element of resultsId array to assetIds
    resultsId.forEach(id => {
      if (id != null) {
        assetIds.push(parseInt(id));
      }
    });
  }

  const requestBody = {
    name,
    scopes: ['assets:read'],
    assetIds,
  };

  try {
    const response = await fetch(`${CESIUM_API_BASE}/v2/tokens`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${cesiumToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        // Continue with errorText
      }
      
      throw new Error(errorData.message || errorText || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to create Cesium token:', error);
    throw new Error(`Failed to create token: ${error.message}`);
  }
};

/**
 * List all Cesium Ion access tokens for a specific asset
 * @param {string} assetId - Asset ID to filter tokens
 * @param {string} cesiumToken - Master Cesium token for API calls
 * @returns {Promise<Array>} Array of token objects
 */
export const listCesiumTokens = async (assetId, cesiumToken) => {
  try {
    const response = await fetch(`${CESIUM_API_BASE}/v2/tokens`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${cesiumToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Filter tokens that have access to the specific asset
    const filteredTokens = data.items?.filter(token => {
      return token.assetIds && token.assetIds.includes(parseInt(assetId));
    }) || [];

    return filteredTokens;
  } catch (error) {
    console.error('Failed to list Cesium tokens:', error);
    throw new Error(`Failed to list tokens: ${error.message}`);
  }
};

/**
 * Delete a Cesium Ion access token
 * @param {string} tokenId - Token ID to delete
 * @param {string} cesiumToken - Master Cesium token for API calls
 * @returns {Promise<void>}
 */
export const deleteCesiumToken = async (tokenId, cesiumToken) => {
  try {
    const response = await fetch(`${CESIUM_API_BASE}/v2/tokens/${tokenId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${cesiumToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Failed to delete Cesium token:', error);
    throw new Error(`Failed to delete token: ${error.message}`);
  }
};

/**
 * Validate if a Cesium token has access to a specific asset
 * @param {string} token - Token to validate
 * @param {string} assetId - Asset ID to check access for
 * @returns {Promise<boolean>} True if token has access
 */
export const validateCesiumTokenAccess = async (token, assetId) => {
  try {
    const response = await fetch(`${CESIUM_API_BASE}/v1/assets/${assetId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to validate Cesium token:', error);
    return false;
  }
};

/**
 * Get asset information using a Cesium token
 * @param {string} assetId - Asset ID
 * @param {string} token - Cesium token
 * @returns {Promise<Object>} Asset data
 */
export const getCesiumAsset = async (assetId, token) => {
  try {
    const response = await fetch(`${CESIUM_API_BASE}/v1/assets/${assetId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to get Cesium asset:', error);
    throw new Error(`Failed to get asset: ${error.message}`);
  }
};

/**
 * Update the token_cesium and share_cesium_short_url fields in Strapi data collection
 * @param {string} dataId - The Strapi data record ID
 * @param {string} tokenId - The Cesium token ID to store
 * @param {string} shortUrlId - The short URL ID to store (optional)
 * @param {string} jwt - Strapi JWT token
 * @returns {Promise<Object>} Strapi response
 */
export const updateDataTokenCesium = async (dataId, tokenId, shortUrlId, jwt) => {
  try {
    const updateData = {
      token_cesium: tokenId
    };
    
    // Only update short URL if provided
    if (shortUrlId !== undefined) {
      updateData.share_cesium_short_url = shortUrlId;
    }

    const response = await fetch(`${config.apiUrl}/api/datas/${dataId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: updateData
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to update Cesium share data in Strapi: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to update Cesium share data in Strapi:', error);
    throw error;
  }
};


/**
 * Get existing share data for a file
 * @param {Object} fileData - File data from Strapi
 * @returns {Object|null} Existing share data or null
 */
export const getExistingShareData = (fileData) => {
  const tokenCesium = fileData.attributes.token_cesium;
  const shortUrlRelation = fileData.attributes.share_cesium_short_url;
  
  // Handle populated short URL data (relation object)
  const shortUrlData = shortUrlRelation?.data?.attributes || shortUrlRelation;
  
  if (tokenCesium && shortUrlData) {
    return {
      tokenId: tokenCesium,
      shortUrlData: shortUrlData,
      hasExistingShare: true
    };
  }
  
  return {
    hasExistingShare: false,
    tokenId: tokenCesium,
    shortUrlData: shortUrlData
  };
};

/**
 * Custom hook for managing Cesium tokens
 * @param {string} assetId - Asset ID
 * @returns {Object} Token management functions and state
 */
export const useCesiumTokens = (assetId) => {
  const { cesium_token } = useContext(CloudContext);
  const { jwt } = useContext(AuthContext);

  const createToken = useCallback(async (tokenData, dataId) => {
    if (!cesium_token) throw new Error('No Cesium token available');
    if (!jwt) throw new Error('No Strapi JWT available');
    if (!dataId) throw new Error('Data ID is required');

    const newToken = await createCesiumToken(tokenData, cesium_token);

    // Generate the full share URL - include gaussian splatting ID, products, and transformations if available
    const baseUrl = window.location.origin;
    let fullShareUrl;

    // Encode products array as URL parameter if present
    const encodedProducts = tokenData.resultsId && tokenData.resultsId.length > 0
      ? encodeURIComponent(JSON.stringify(tokenData.resultsId))
      : null;

    // Transformation segment — encode both transforms as a single /t/:pair path segment
    const hasTransform = tokenData.transformationPointsStr != null || tokenData.transformationGsStr != null;
    const tSegment = hasTransform
      ? `/t/${encodePair(tokenData.transformationPointsStr ?? '', tokenData.transformationGsStr ?? '')}`
      : '';

    if (encodedProducts) {
      // URLs with products
      fullShareUrl = tokenData.gaussianSplattingId
        ? `${baseUrl}/public/cesium/${tokenData.assetId}/${tokenData.gaussianSplattingId}/products/${encodedProducts}${tSegment}/${newToken.token}`
        : `${baseUrl}/public/cesium/${tokenData.assetId}/products/${encodedProducts}${tSegment}/${newToken.token}`;
    } else {
      // URLs without products
      fullShareUrl = tokenData.gaussianSplattingId
        ? `${baseUrl}/public/cesium/${tokenData.assetId}/${tokenData.gaussianSplattingId}${tSegment}/${newToken.token}`
        : `${baseUrl}/public/cesium/${tokenData.assetId}${tSegment}/${newToken.token}`;
    }

    // Create short URL using existing urlShortener function
    const shortUrlResponse = await createShortUrl(fullShareUrl, jwt);
    
    // Update both token_cesium and share_cesium_short_url fields
    await updateDataTokenCesium(dataId, newToken.id, shortUrlResponse.id, jwt);
    
    return {
      ...newToken,
      shortUrl: shortUrlResponse.shortUrl,
      shortCode: shortUrlResponse.shortCode,
      fullUrl: fullShareUrl
    };
  }, [cesium_token, jwt]);

  const deleteToken = useCallback(async (tokenId, dataId, fileData) => {
    if (!cesium_token) throw new Error('No Cesium token available');
    if (!jwt) throw new Error('No Strapi JWT available');
    if (!dataId) throw new Error('Data ID is required');
    
    // Get the shortUrl ID from the file data to delete it
    const shortUrlRelation = fileData?.attributes?.share_cesium_short_url;
    const shortUrlId = shortUrlRelation?.data?.id || shortUrlRelation?.id;
    
    // Delete the short URL record first if it exists
    if (shortUrlId) {
      try {
        await deleteShortUrl(shortUrlId, jwt);
      } catch (error) {
        // Continue with token deletion even if short URL deletion fails
      }
    }
    
    // Delete from Cesium Ion
    await deleteCesiumToken(tokenId, cesium_token);
    
    // Clear both token_cesium and share_cesium_short_url fields
    await updateDataTokenCesium(dataId, null, null, jwt);
    
    return true;
  }, [cesium_token, jwt]);

  return {
    createToken,
    deleteToken
  };
};